import type * as Phaser from "phaser"

/** High-level game actions that can be triggered by various physical inputs. */
export type InputAction =
  | "FLAP"
  | "PAUSE"
  | "CONFIRM"
  | "BACK"
  | "ADVANCE"
  | "UP"
  | "DOWN"
  | "LEFT"
  | "RIGHT"
  | "REROLL"
  | "DEBUG_TOGGLE"
  | "DEBUG_PAUSE"
  | "DEBUG_PHYSICS"
  | "DEBUG_INSPECT"
  | "DEBUG_SFX"

/** Physical input types. */
export type InputType = "keyboard" | "gamepad" | "pointer"

/** Controller brand for specific button labels. */
export type InputBrand = "generic" | "xbox" | "playstation" | "nintendo"

/** Mapping of a physical input to an action. */
export interface InputMapping {
  action: InputAction
  type: InputType
  key?: string // For keyboard (e.g., "SPACE", "ENTER", "ESC")
  button?: string // For gamepad (e.g., "A", "B", "START")
}

/**
 * Centralized input configuration.
 * Edit this to change which keys/buttons trigger which actions.
 */
export const GLOBAL_INPUT_MAP: InputMapping[] = [
  // Flap / Primary Action
  { action: "FLAP", type: "keyboard", key: "SPACE" },
  { action: "FLAP", type: "keyboard", key: "ENTER" },
  { action: "FLAP", type: "keyboard", key: "UP" },

  { action: "FLAP", type: "gamepad", button: "A" },
  { action: "FLAP", type: "gamepad", button: "B" },
  { action: "FLAP", type: "gamepad", button: "X" },
  { action: "FLAP", type: "gamepad", button: "Y" },
  { action: "FLAP", type: "pointer" },

  // Navigation / Menus
  { action: "PAUSE", type: "keyboard", key: "ESC" },
  { action: "PAUSE", type: "keyboard", key: "P" },
  { action: "PAUSE", type: "gamepad", button: "START" },

  { action: "CONFIRM", type: "keyboard", key: "ENTER" },
  { action: "CONFIRM", type: "keyboard", key: "SPACE" },
  { action: "CONFIRM", type: "gamepad", button: "A" },

  { action: "BACK", type: "keyboard", key: "ESC" },
  { action: "BACK", type: "gamepad", button: "B" },

  { action: "ADVANCE", type: "keyboard", key: "RIGHT" },
  { action: "ADVANCE", type: "keyboard", key: "ENTER" },
  { action: "ADVANCE", type: "keyboard", key: "SPACE" },
  { action: "ADVANCE", type: "gamepad", button: "A" },

  { action: "LEFT", type: "keyboard", key: "LEFT" },
  { action: "LEFT", type: "keyboard", key: "A" },

  { action: "RIGHT", type: "keyboard", key: "RIGHT" },
  { action: "RIGHT", type: "keyboard", key: "D" },

  { action: "UP", type: "keyboard", key: "UP" },
  { action: "UP", type: "keyboard", key: "W" },

  { action: "DOWN", type: "keyboard", key: "DOWN" },
  { action: "DOWN", type: "keyboard", key: "S" },

  { action: "REROLL", type: "keyboard", key: "R" },

  // Debug Actions
  { action: "DEBUG_TOGGLE", type: "keyboard", key: "F12" },
  { action: "DEBUG_PAUSE", type: "keyboard", key: "F9" },
  { action: "DEBUG_PHYSICS", type: "keyboard", key: "F8" },
  { action: "DEBUG_INSPECT", type: "keyboard", key: "F11" },
  { action: "DEBUG_SFX", type: "keyboard", key: "F7" },
]

export interface InputEvents {
  onDown: (action: InputAction, event?: any) => void
  onUp?: (action: InputAction, event?: any) => void
}

type ListenerTarget = {
  on: (event: string, handler: (...args: any[]) => void) => void
  off?: (event: string, handler: (...args: any[]) => void) => void
  removeListener?: (event: string, handler: (...args: any[]) => void) => void
}

type ListenerEntry = {
  target: ListenerTarget
  event: string
  handler: (...args: any[]) => void
}

export interface PointerHandlers {
  pointerover?: (...args: any[]) => void
  pointerout?: (...args: any[]) => void
  pointerdown?: (...args: any[]) => void
  pointerup?: (...args: any[]) => void
  pointermove?: (...args: any[]) => void
}

const GAMEPAD_BUTTON_NAMES: Record<number, string> = {
  0: "A",
  1: "B",
  2: "X",
  3: "Y",
  4: "L1",
  5: "R1",
  6: "L2",
  7: "R2",
  8: "BACK",
  9: "START",
  12: "UP",
  13: "DOWN",
  14: "LEFT",
  15: "RIGHT",
}

const PS_BUTTON_LABELS: Record<string, string> = {
  A: "CROSS",
  B: "CIRCLE",
  X: "SQUARE",
  Y: "TRIANGLE",
  L1: "L1",
  R1: "R1",
  L2: "L2",
  R2: "R2",
  BACK: "SHARE",
  START: "OPTIONS",
}

type DirectionalAction = "LEFT" | "RIGHT" | "UP" | "DOWN"

type DirectionState = Record<DirectionalAction, boolean>

const DEFAULT_DIRECTION_STATE: DirectionState = {
  LEFT: false,
  RIGHT: false,
  UP: false,
  DOWN: false,
}

const GAMEPAD_DIRECTION_ACTIONS: Record<DirectionalAction, InputAction[]> = {
  LEFT: ["LEFT"],
  RIGHT: ["RIGHT", "ADVANCE"],
  UP: ["UP"],
  DOWN: ["DOWN"],
}

/**
 * A helper class to register input listeners based on the GLOBAL_INPUT_MAP.
 * Can be instantiated per-scene to handle scene-specific input logic.
 */
export class InputManager {
  private scene: Phaser.Scene
  private handlers: InputEvents
  private listeners: ListenerEntry[] = []
  private gamepadDirectionStates = new Map<number, DirectionState>()

  /** The last type of input device used by the player. Useful for dynamic UI hints. */
  public static lastUsedType: InputType =
    typeof window !== "undefined" &&
    ("ontouchstart" in window || navigator.maxTouchPoints > 0)
      ? "pointer"
      : "keyboard"
  /** The brand of the last used controller. */
  public static lastUsedBrand: InputBrand = "generic"

  constructor(scene: Phaser.Scene, handlers: InputEvents) {
    this.scene = scene
    this.handlers = handlers
    this.setupListeners()
  }

  private setLastUsed(type: InputType, brand: InputBrand = "generic"): void {
    InputManager.lastUsedType = type
    if (type === "gamepad") {
      InputManager.lastUsedBrand = brand
    }
  }

  private detectBrand(pad: Phaser.Input.Gamepad.Gamepad): InputBrand {
    const id = pad.id.toLowerCase()
    if (
      id.includes("xbox") ||
      id.includes("microsoft") ||
      id.includes("x-box")
    ) {
      return "xbox"
    }
    if (
      id.includes("playstation") ||
      id.includes("dualshock") ||
      id.includes("dualsense") ||
      id.includes("sony") ||
      id.includes("wireless controller")
    ) {
      return "playstation"
    }
    if (
      id.includes("nintendo") ||
      id.includes("pro controller") ||
      id.includes("joy-con")
    ) {
      return "nintendo"
    }
    return "generic"
  }

  private setupListeners(): void {
    const keyboard = this.scene.input.keyboard
    const gamepad = this.scene.input.gamepad
    const gamepadMappings = GLOBAL_INPUT_MAP.filter(
      (mapping) => mapping.type === "gamepad",
    )
    const bindPad = (pad: Phaser.Input.Gamepad.Gamepad): void => {
      const brand = this.detectBrand(pad)
      const downHandler = (index: number) => {
        const buttonName = GAMEPAD_BUTTON_NAMES[index]
        if (!buttonName) return

        this.setLastUsed("gamepad", brand)
        for (const mapping of gamepadMappings) {
          if (mapping.button === buttonName) {
            this.handlers.onDown(mapping.action)
          }
        }
      }
      const upHandler = (index: number) => {
        const buttonName = GAMEPAD_BUTTON_NAMES[index]
        if (!buttonName) return

        this.setLastUsed("gamepad", brand)
        for (const mapping of gamepadMappings) {
          if (mapping.button === buttonName) {
            this.handlers.onUp?.(mapping.action)
          }
        }
      }

      pad.on("down", downHandler)
      pad.on("up", upHandler)
      this.listeners.push({ target: pad, event: "down", handler: downHandler })
      this.listeners.push({ target: pad, event: "up", handler: upHandler })
    }

    if (gamepad) {
      const connectedHandler = (pad: Phaser.Input.Gamepad.Gamepad) => {
        bindPad(pad)
        this.gamepadDirectionStates.set(pad.index, {
          ...DEFAULT_DIRECTION_STATE,
        })
      }
      const disconnectedHandler = (pad: Phaser.Input.Gamepad.Gamepad) => {
        this.releaseDirectionalActions(pad.index)
        this.gamepadDirectionStates.delete(pad.index)
      }

      gamepad.on("connected", connectedHandler)
      gamepad.on("disconnected", disconnectedHandler)
      this.listeners.push({
        target: gamepad,
        event: "connected",
        handler: connectedHandler,
      })
      this.listeners.push({
        target: gamepad,
        event: "disconnected",
        handler: disconnectedHandler,
      })
      const updateHandler = () => {
        this.pollGamepadDirections()
      }
      this.scene.events.on("update", updateHandler)
      this.listeners.push({
        target: this.scene.events,
        event: "update",
        handler: updateHandler,
      })

      for (const pad of [
        gamepad.pad1,
        gamepad.pad2,
        gamepad.pad3,
        gamepad.pad4,
      ]) {
        if (pad) bindPad(pad)
      }
    }

    for (const mapping of GLOBAL_INPUT_MAP) {
      if (mapping.type === "keyboard" && keyboard) {
        const downHandler = (keyOrEvent: unknown, event?: KeyboardEvent) => {
          const keyboardEvent =
            event ?? (keyOrEvent instanceof KeyboardEvent ? keyOrEvent : null)
          if (!keyboardEvent) return
          if (keyboardEvent.repeat) return
          this.setLastUsed("keyboard")
          this.handlers.onDown(mapping.action, keyboardEvent)
        }
        const upHandler = () => {
          this.setLastUsed("keyboard")
          this.handlers.onUp?.(mapping.action)
        }

        keyboard.on(`keydown-${mapping.key}`, downHandler)
        keyboard.on(`keyup-${mapping.key}`, upHandler)

        this.listeners.push({
          target: keyboard,
          event: `keydown-${mapping.key}`,
          handler: downHandler,
        })
        this.listeners.push({
          target: keyboard,
          event: `keyup-${mapping.key}`,
          handler: upHandler,
        })
      }

      if (mapping.type === "pointer") {
        const downHandler = (
          pointer: Phaser.Input.Pointer,
          currentlyOver: any[],
        ) => {
          this.setLastUsed("pointer")
          this.handlers.onDown(mapping.action, { pointer, currentlyOver })
        }
        const upHandler = (
          pointer: Phaser.Input.Pointer,
          currentlyOver: any[],
        ) => {
          this.setLastUsed("pointer")
          this.handlers.onUp?.(mapping.action, { pointer, currentlyOver })
        }

        this.scene.input.on("pointerdown", downHandler)
        this.scene.input.on("pointerup", upHandler)
        this.listeners.push({
          target: this.scene.input,
          event: "pointerdown",
          handler: downHandler,
        })
        this.listeners.push({
          target: this.scene.input,
          event: "pointerup",
          handler: upHandler,
        })
      }
    }
  }

  /** Returns a physical button/key label for a generic action based on the last used input device. */
  public static getLabelForAction(action: InputAction): string {
    const type = InputManager.lastUsedType
    const brand = InputManager.lastUsedBrand

    if (type === "pointer") {
      return "TAP"
    }

    const mapping = GLOBAL_INPUT_MAP.find(
      (m) => m.action === action && m.type === type,
    )

    if (type === "gamepad") {
      const btn = mapping?.button || "A"
      if (brand === "playstation") {
        return PS_BUTTON_LABELS[btn] || btn
      }
      return btn
    }

    return mapping?.key || "SPACE"
  }

  public bindPointer(target: ListenerTarget, handlers: PointerHandlers): void {
    for (const [event, handler] of Object.entries(handlers)) {
      if (!handler) continue
      target.on(event, handler)
      this.listeners.push({ target, event, handler })
    }
  }

  public bindScenePointer(
    event: string,
    handler: (...args: any[]) => void,
  ): void {
    this.scene.input.on(event, handler)
    this.listeners.push({ target: this.scene.input, event, handler })
  }

  public destroy(): void {
    for (const listener of this.listeners) {
      listener.target.off?.(listener.event, listener.handler)
      listener.target.removeListener?.(listener.event, listener.handler)
    }

    this.listeners = []
  }

  private pollGamepadDirections(): void {
    const gamepad = this.scene.input.gamepad
    if (!gamepad) return

    for (const pad of [
      gamepad.pad1,
      gamepad.pad2,
      gamepad.pad3,
      gamepad.pad4,
    ]) {
      if (!pad) continue

      const previous = this.gamepadDirectionStates.get(pad.index) ?? {
        ...DEFAULT_DIRECTION_STATE,
      }
      const next = this.getDirectionalStateFromPad(pad)

      const isDirectionalChange =
        next.LEFT !== previous.LEFT ||
        next.RIGHT !== previous.RIGHT ||
        next.UP !== previous.UP ||
        next.DOWN !== previous.DOWN

      if (isDirectionalChange) {
        this.setLastUsed("gamepad", this.detectBrand(pad))
      }

      this.syncDirectionalAction("LEFT", previous.LEFT, next.LEFT, pad)
      this.syncDirectionalAction("RIGHT", previous.RIGHT, next.RIGHT, pad)
      this.syncDirectionalAction("UP", previous.UP, next.UP, pad)
      this.syncDirectionalAction("DOWN", previous.DOWN, next.DOWN, pad)

      this.gamepadDirectionStates.set(pad.index, next)
    }
  }

  private getDirectionalStateFromPad(
    pad: Phaser.Input.Gamepad.Gamepad,
  ): DirectionState {
    const deadzone = 0.3
    const stickX = pad.leftStick.x
    const stickY = pad.leftStick.y

    return {
      LEFT: pad.left || stickX < -deadzone,
      RIGHT: pad.right || stickX > deadzone,
      UP: pad.up || stickY < -deadzone,
      DOWN: pad.down || stickY > deadzone,
    }
  }

  private syncDirectionalAction(
    action: DirectionalAction,
    wasActive: boolean,
    isActive: boolean,
    pad: Phaser.Input.Gamepad.Gamepad,
  ): void {
    if (wasActive === isActive) return

    const brand = this.detectBrand(pad)

    for (const mappedAction of GAMEPAD_DIRECTION_ACTIONS[action]) {
      if (isActive) {
        this.setLastUsed("gamepad", brand)
        this.handlers.onDown(mappedAction)
      } else {
        this.setLastUsed("gamepad", brand)
        this.handlers.onUp?.(mappedAction)
      }
    }
  }

  private releaseDirectionalActions(padIndex: number): void {
    const previous = this.gamepadDirectionStates.get(padIndex)
    if (!previous) return

    for (const action of Object.keys(previous) as DirectionalAction[]) {
      if (!previous[action]) continue
      for (const mappedAction of GAMEPAD_DIRECTION_ACTIONS[action]) {
        this.handlers.onUp?.(mappedAction)
      }
    }
  }
}
